import { getActionApiUrl } from "../config/api";
import { buildApiHeaders } from "./api";
import { message } from "antd";

/**
 * تابع کمکی برای تبدیل Unicode escape sequences به متن فارسی
 * این تابع برای دیباگ و نمایش متن فارسی در console استفاده می‌شود
 * @param {string} str - رشته با Unicode escape sequences
 * @returns {string} - رشته با کاراکترهای فارسی
 */
export const decodeUnicodeEscapes = (str) => {
  if (typeof str !== "string") return str;
  return str.replace(/\\u([0-9a-fA-F]{4})/g, (match, code) => {
    return String.fromCharCode(parseInt(code, 16));
  });
};

/**
 * تابع کمکی برای نمایش object با متن فارسی قابل خواندن در console
 * @param {any} obj - object برای نمایش
 * @param {string} label - برچسب برای console.log
 */
export const logWithPersianText = (obj, label = "Data") => {
  console.log(`📋 ${label}:`, obj);
  console.log(`📋 ${label} (Formatted):`, JSON.stringify(obj, null, 2));
};

export class ActionValidationError extends Error {
  constructor(message, errors = {}) {
    super(message);
    this.name = "ActionValidationError";
    this.errors = errors;
    this.status = 422;
  }
}

export const actionApiRequest = async (endpoint = "", options = {}) => {
  const actionToken = localStorage.getItem("actionToken");

  if (!actionToken) {
    throw new Error(
      "توکن ماژول اقدام یافت نشد. لطفاً ابتدا وارد ماژول اقدام شوید."
    );
  }

  const url = getActionApiUrl(endpoint);

  const isFormData = options.body instanceof FormData;

  const headers = buildApiHeaders(options);
  headers.Authorization = `Bearer ${actionToken}`;

  let debugBody;
  if (isFormData) {
    debugBody = "[FormData]";
  } else if (typeof options.body === "string") {
    try {
      debugBody = JSON.parse(options.body);
    } catch {
      debugBody = options.body;
    }
  } else {
    debugBody = options.body;
  }

  console.log("🔵 Action API Request:", {
    url,
    method: options.method || "GET",
    hasToken: !!actionToken,
    body: debugBody,
  });

  try {
    const response = await fetch(url, {
      ...options,
      headers,
    });

    console.log("🟢 Action API Response:", {
      url,
      status: response.status,
      statusText: response.statusText,
      ok: response.ok,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      console.error("🔴 Action API Error:", {
        url,
        status: response.status,
        errorData,
      });
      // نمایش خطا با متن فارسی قابل خواندن
      console.error(
        "🔴 Action API Error (Formatted):",
        JSON.stringify(errorData, null, 2)
      );

      // هندل خطاهای 401 (Unauthorized) - برگشت به داشبورد ERP
      if (response.status === 401) {
        try {
          // جلوگیری از ریدایرکت‌های تکراری
          if (!window.__ACTION_UNAUTHORIZED_REDIRECTING__) {
            window.__ACTION_UNAUTHORIZED_REDIRECTING__ = true;
            // پاک کردن توکن action
            localStorage.removeItem('actionToken');
            localStorage.removeItem('actionUserData');
            // هدایت به داشبورد ERP
            if (window.location.pathname !== '/dashboard') {
              window.location.replace('/dashboard');
            }
          }
        } catch (e) {
          console.error('Error during 401 redirect:', e);
        }
        throw new Error(
          errorData.message ||
            errorData.error?.message ||
            'Unauthorized - دسترسی غیرمجاز'
        );
      }

      // هندل خطاهای 422 (Validation)
      if (response.status === 422 && errorData.errors) {
        throw new ActionValidationError(
          errorData.message || "خطا در اعتبارسنجی داده‌ها",
          errorData.errors
        );
      }

      throw new Error(
        errorData.message ||
          errorData.error?.message ||
          `HTTP error! status: ${response.status}`
      );
    }

    // پارس کردن پاسخ و لاگ کردن با متن فارسی قابل خواندن
    const responseData = await response.json();

    // نمایش داده‌های پارس شده با متن فارسی قابل خواندن در console
    console.log("📦 Action API Response Data (Parsed):", responseData);
    // نمایش فرمت شده برای خوانایی بهتر
    console.log(
      "📦 Action API Response Data (Formatted):",
      JSON.stringify(responseData, null, 2)
    );

    return responseData;
  } catch (error) {
    console.error("🔴 Action API Request Failed:", {
      url,
      error: error.message,
      errorObject: error,
    });
    throw error;
  }
};

// تابع‌های API برای ماژول action
export const actionApi = {
  // داشبورد اقدام‌ها
  getActionDashboard: async () => {
    return actionApiRequest("actions/dashboard", { method: "GET" });
  },

  // لیست اقدام‌های من
  getMyActions: async ({
    page = 1,
    per_page = 15,
    status,
    priority,
    type,
    raci,
    due_from,
    due_to,
    search,
    sort,
    dir,
    saved_filter_id,
    bookmarked,
    requester_id,
  } = {}) => {
    const params = new URLSearchParams();
    if (page) params.append("page", page);
    if (per_page) params.append("per_page", per_page);

    // آرایه‌ها یا رشته‌های جدا شده با کاما
    if (status) {
      if (Array.isArray(status)) {
        status.forEach((s) => params.append("status[]", s));
      } else if (typeof status === "string" && status.includes(",")) {
        params.append("status", status);
      } else {
        params.append("status[]", status);
      }
    }
    if (priority) {
      if (Array.isArray(priority)) {
        priority.forEach((p) => params.append("priority[]", p));
      } else if (typeof priority === "string" && priority.includes(",")) {
        params.append("priority", priority);
      } else {
        params.append("priority[]", priority);
      }
    }
    if (type) {
      if (Array.isArray(type)) {
        type.forEach((t) => params.append("type[]", t));
      } else if (typeof type === "string" && type.includes(",")) {
        params.append("type", type);
      } else {
        params.append("type[]", type);
      }
    }
    if (raci) {
      if (Array.isArray(raci)) {
        raci.forEach((r) => params.append("raci[]", r));
      } else if (typeof raci === "string" && raci.includes(",")) {
        params.append("raci", raci);
      } else {
        params.append("raci[]", raci);
      }
    }
    if (due_from) params.append("due_from", due_from);
    if (due_to) params.append("due_to", due_to);
    if (search) params.append("search", search);
    if (sort) params.append("sort", sort);
    if (dir) params.append("dir", dir);
    if (saved_filter_id) params.append("saved_filter_id", saved_filter_id);
    if (bookmarked !== undefined) params.append("bookmarked", bookmarked);
    if (requester_id) params.append("requester_id", requester_id);

    const qs = params.toString();
    const endpoint = qs ? `actions/my-actions?${qs}` : "actions/my-actions";
    return actionApiRequest(endpoint, { method: "GET" });
  },

  // لیست اقدام‌ها (index)
  getActions: async ({
    page = 1,
    per_page = 15,
    perPage, // backwards compat
    status,
    priority,
    action_type,
    source_module,
    requester_id,
    assigned_to,
    raci_role,
    raci_user_id,
    search,
  } = {}) => {
    const params = new URLSearchParams();
    params.append("page", page);
    params.append("per_page", perPage ?? per_page);

    const optionalParams = {
      status,
      priority,
      action_type,
      source_module,
      requester_id,
      assigned_to,
      raci_role,
      raci_user_id,
      search,
    };

    Object.entries(optionalParams).forEach(([key, val]) => {
      if (val !== null && val !== undefined && val !== "") {
        params.append(key, String(val));
      }
    });

    const endpoint = `actions?${params.toString()}`;
    return actionApiRequest(endpoint, { method: "GET" });
  },

  // دریافت یک اقدام خاص
  getActionById: async (id) => {
    return actionApiRequest(`actions/${id}`, { method: "GET" });
  },

  // ایجاد اقدام جدید
  createAction: async (data) => {
    return actionApiRequest("actions", {
      method: "POST",
      body: JSON.stringify(data),
    });
  },

  // به‌روزرسانی اقدام
  updateAction: async (id, data) => {
    return actionApiRequest(`actions/${id}`, {
      method: "PUT",
      body: JSON.stringify(data),
    });
  },

  // حذف اقدام
  deleteAction: async (id) => {
    return actionApiRequest(`actions/${id}`, { method: "DELETE" });
  },

  // بوکمارک/آنبوکمارک اقدام
  toggleActionBookmark: async (actionId) => {
    return actionApiRequest(`actions/${actionId}/bookmark`, {
      method: "POST",
    });
  },

  // -------------------- Accept --------------------
  // پذیرش یک اقدام توسط کاربر مسئول_اجرا
  acceptAction: async (actionId, comment = "") => {
    return actionApiRequest(`actions/${actionId}/accept`, {
      method: "POST",
      body: JSON.stringify({ comment }),
    });
  },

  // -------------------- Progress --------------------
  // به‌روزرسانی پیشرفت اقدام توسط کاربر مسئول_اجرا
  updateActionProgress: async (actionId, percent_complete, comment = "") => {
    return actionApiRequest(`actions/${actionId}/progress`, {
      method: "POST",
      body: JSON.stringify({
        percent_complete,
        comment,
      }),
    });
  },

  // -------------------- Close request --------------------
  // ارسال درخواست بستن اقدام توسط کاربر مسئول_اجرا
  requestCloseAction: async (actionId, comment) => {
    return actionApiRequest(`actions/${actionId}/request-close`, {
      method: "POST",
      body: JSON.stringify({ comment }),
    });
  },

  // -------------------- Close --------------------
  // بستن اقدام توسط کاربر پاسخگو
  closeAction: async (actionId, comment = "") => {
    return actionApiRequest(`actions/${actionId}/close`, {
      method: "POST",
      body: JSON.stringify({ comment }),
    });
  },

  // -------------------- Comments --------------------
  // دریافت لیست نظرات اقدام
  getActionComments: async (
    actionId,
    { user_id, from_date, to_date, sort, root_only, per_page } = {}
  ) => {
    const params = new URLSearchParams();
    const optionalParams = {
      user_id,
      from_date,
      to_date,
      sort,
      root_only,
      per_page,
    };
    Object.entries(optionalParams).forEach(([key, val]) => {
      if (val !== null && val !== undefined && val !== "") {
        params.append(key, String(val));
      }
    });
    const qs = params.toString();
    const endpoint = qs
      ? `actions/${actionId}/comments?${qs}`
      : `actions/${actionId}/comments`;
    return actionApiRequest(endpoint, { method: "GET" });
  },

  // ایجاد نظر جدید برای اقدام
  createActionComment: async (actionId, { content, parent_id } = {}) => {
    return actionApiRequest(`actions/${actionId}/comments`, {
      method: "POST",
      body: JSON.stringify({ content, parent_id }),
    });
  },

  // دریافت یک نظر خاص
  getActionComment: async (actionId, commentId) => {
    return actionApiRequest(`actions/${actionId}/comments/${commentId}`, {
      method: "GET",
    });
  },

  // به‌روزرسانی نظر
  updateActionComment: async (actionId, commentId, content) => {
    return actionApiRequest(`actions/${actionId}/comments/${commentId}`, {
      method: "PUT",
      body: JSON.stringify({ content }),
    });
  },

  // حذف نظر
  deleteActionComment: async (actionId, commentId) => {
    return actionApiRequest(`actions/${actionId}/comments/${commentId}`, {
      method: "DELETE",
    });
  },

  // دریافت لیست پاسخ‌های یک نظر
  getActionCommentReplies: async (actionId, commentId) => {
    return actionApiRequest(
      `actions/${actionId}/comments/${commentId}/replies`,
      {
        method: "GET",
      }
    );
  },

  // -------------------- Requests --------------------
  // ایجاد درخواست جدید برای اقدام یا آیتم اقدام
  createActionRequest: async (actionId, data) => {
    return actionApiRequest(`actions/${actionId}/requests`, {
      method: "POST",
      body: JSON.stringify(data),
    });
  },

  // دریافت لیست درخواست‌های یک اقدام
  getActionRequests: async (
    actionId,
    { status, request_type, requested_by } = {}
  ) => {
    const params = new URLSearchParams();
    const optionalParams = { status, request_type, requested_by };

    Object.entries(optionalParams).forEach(([key, val]) => {
      if (val !== null && val !== undefined && val !== "") {
        params.append(key, String(val));
      }
    });

    const qs = params.toString();
    const endpoint = qs
      ? `actions/${actionId}/requests?${qs}`
      : `actions/${actionId}/requests`;
    return actionApiRequest(endpoint, { method: "GET" });
  },

  // -------------------- Action Items --------------------
  // دریافت لیست آیتم‌های یک اقدام
  getActionItems: async (actionId) => {
    return actionApiRequest(`actions/${actionId}/items`, {
      method: "GET",
    });
  },

  // ایجاد آیتم اقدام جدید
  createActionItem: async (actionId, data) => {
    return actionApiRequest(`actions/${actionId}/items`, {
      method: "POST",
      body: JSON.stringify(data),
    });
  },

  // نمایش یک آیتم اقدام
  getActionItem: async (actionId, itemId) => {
    return actionApiRequest(`actions/${actionId}/items/${itemId}`, {
      method: "GET",
    });
  },

  // به‌روزرسانی آیتم اقدام
  updateActionItem: async (actionId, itemId, data) => {
    return actionApiRequest(`actions/${actionId}/items/${itemId}`, {
      method: "PUT",
      body: JSON.stringify(data),
    });
  },

  // حذف آیتم اقدام
  deleteActionItem: async (actionId, itemId) => {
    return actionApiRequest(`actions/${actionId}/items/${itemId}`, {
      method: "DELETE",
    });
  },

  // شروع آیتم اقدام
  startActionItem: async (actionId, itemId) => {
    return actionApiRequest(`actions/${actionId}/items/${itemId}/start`, {
      method: "POST",
    });
  },

  // تکمیل/اتمام آیتم اقدام (تنظیم actual_end_date در بک‌اند)
  completeActionItem: async (actionId, itemId) => {
    return actionApiRequest(`actions/${actionId}/items/${itemId}/complete`, {
      method: "POST",
    });
  },

  // -------------------- Action Item RACI --------------------
  // دریافت لیست RACIهای یک آیتم اقدام
  getActionItemRacis: async (actionId, itemId) => {
    return actionApiRequest(`actions/${actionId}/items/${itemId}/racis`, {
      method: "GET",
    });
  },

  // ایجاد تخصیص‌های RACI برای آیتم اقدام
  createActionItemRacis: async (actionId, itemId, assignments) => {
    return actionApiRequest(`actions/${actionId}/items/${itemId}/racis`, {
      method: "POST",
      body: JSON.stringify({ assignments }),
    });
  },

  // حذف یک RACI از آیتم اقدام
  deleteActionItemRaci: async (actionId, itemId, raciId) => {
    return actionApiRequest(
      `actions/${actionId}/items/${itemId}/racis/${raciId}`,
      {
        method: "DELETE",
      }
    );
  },

  // -------------------- Action Item Resources --------------------
  // دریافت لیست منابع یک آیتم اقدام
  getActionItemResources: async (actionId, itemId) => {
    return actionApiRequest(`actions/${actionId}/items/${itemId}/resources`, {
      method: "GET",
    });
  },

  // ایجاد منبع برای آیتم اقدام
  createActionItemResource: async (actionId, itemId, data) => {
    return actionApiRequest(`actions/${actionId}/items/${itemId}/resources`, {
      method: "POST",
      body: JSON.stringify(data),
    });
  },

  // دریافت اطلاعات یک منبع خاص برای آیتم
  getActionItemResource: async (actionId, itemId, resourceId) => {
    return actionApiRequest(
      `actions/${actionId}/items/${itemId}/resources/${resourceId}`,
      {
        method: "GET",
      }
    );
  },

  // به‌روزرسانی منبع آیتم اقدام
  updateActionItemResource: async (actionId, itemId, resourceId, data) => {
    return actionApiRequest(
      `actions/${actionId}/items/${itemId}/resources/${resourceId}`,
      {
        method: "PUT",
        body: JSON.stringify(data),
      }
    );
  },

  // حذف منبع آیتم اقدام
  deleteActionItemResource: async (actionId, itemId, resourceId) => {
    return actionApiRequest(
      `actions/${actionId}/items/${itemId}/resources/${resourceId}`,
      {
        method: "DELETE",
      }
    );
  },

  // -------------------- Resources --------------------
  // دریافت لیست منابع یک اقدام
  getActionResources: async (actionId, { type } = {}) => {
    const params = new URLSearchParams();
    if (type) {
      params.append("type", type);
    }
    const qs = params.toString();
    const endpoint = qs
      ? `actions/${actionId}/resources?${qs}`
      : `actions/${actionId}/resources`;
    return actionApiRequest(endpoint, { method: "GET" });
  },

  // دریافت خلاصه منابع یک اقدام
  getActionResourcesSummary: async (actionId) => {
    return actionApiRequest(`actions/${actionId}/resources/summary`, {
      method: "GET",
    });
  },

  // ایجاد منبع جدید برای اقدام
  createActionResource: async (actionId, data) => {
    return actionApiRequest(`actions/${actionId}/resources`, {
      method: "POST",
      body: JSON.stringify(data),
    });
  },

  // دریافت اطلاعات یک منبع خاص
  getActionResource: async (actionId, resourceId) => {
    return actionApiRequest(`actions/${actionId}/resources/${resourceId}`, {
      method: "GET",
    });
  },

  // به‌روزرسانی منبع اقدام
  updateActionResource: async (actionId, resourceId, data) => {
    return actionApiRequest(`actions/${actionId}/resources/${resourceId}`, {
      method: "PUT",
      body: JSON.stringify(data),
    });
  },

  // حذف منبع اقدام
  deleteActionResource: async (actionId, resourceId) => {
    return actionApiRequest(`actions/${actionId}/resources/${resourceId}`, {
      method: "DELETE",
    });
  },

  // -------------------- Attachments --------------------
  // دریافت لیست پیوست‌های اقدام
  getActionAttachments: async (actionId, { context } = {}) => {
    const params = new URLSearchParams();
    if (context) {
      params.append("context", context);
    }
    const qs = params.toString();
    const endpoint = qs
      ? `actions/${actionId}/attachments?${qs}`
      : `actions/${actionId}/attachments`;
    return actionApiRequest(endpoint, { method: "GET" });
  },

  // آپلود پیوست برای اقدام یا آیتم اقدام
  uploadActionAttachment: async (
    actionId,
    { file, description, context, action_item_id } = {}
  ) => {
    const formData = new FormData();
    if (!file) {
      throw new Error("فایل برای آپلود الزامی است");
    }
    formData.append("file", file);
    if (description) formData.append("description", description);
    if (context) formData.append("context", context);
    if (action_item_id)
      formData.append("action_item_id", String(action_item_id));

    return actionApiRequest(`actions/${actionId}/attachments`, {
      method: "POST",
      body: formData,
    });
  },

  // دریافت اطلاعات یک پیوست خاص
  getActionAttachment: async (actionId, attachmentId) => {
    return actionApiRequest(`actions/${actionId}/attachments/${attachmentId}`, {
      method: "GET",
    });
  },

  // دانلود پیوست اقدام - برمی‌گرداند Blob
  downloadActionAttachment: async (actionId, attachmentId) => {
    const actionToken = localStorage.getItem("actionToken");
    if (!actionToken) {
      throw new Error(
        "توکن ماژول اقدام یافت نشد. لطفاً ابتدا وارد ماژول اقدام شوید."
      );
    }
    const url = getActionApiUrl(
      `actions/${actionId}/attachments/${attachmentId}/download`
    );

    const response = await fetch(url, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${actionToken}`,
      },
    });

    if (!response.ok) {
      throw new Error(`خطا در دانلود فایل (status: ${response.status})`);
    }

    const blob = await response.blob();
    return blob;
  },

  // دریافت URL پیش‌نمایش پیوست
  getActionAttachmentPreviewUrl: (actionId, attachmentId) => {
    return getActionApiUrl(
      `actions/${actionId}/attachments/${attachmentId}/download`
    );
  },

  // به‌روزرسانی توضیحات پیوست
  updateActionAttachmentDescription: async (
    actionId,
    attachmentId,
    description
  ) => {
    return actionApiRequest(
      `actions/${actionId}/attachments/${attachmentId}/description`,
      {
        method: "PATCH",
        body: JSON.stringify({ description }),
      }
    );
  },

  // حذف پیوست
  deleteActionAttachment: async (actionId, attachmentId) => {
    return actionApiRequest(`actions/${actionId}/attachments/${attachmentId}`, {
      method: "DELETE",
    });
  },

  // -------------------- Action Item Attachments --------------------
  // دریافت لیست پیوست‌های یک آیتم اقدام
  getActionItemAttachments: async (actionId, itemId) => {
    return actionApiRequest(`actions/${actionId}/items/${itemId}/attachments`, {
      method: "GET",
    });
  },

  // آپلود پیوست برای آیتم اقدام
  uploadActionItemAttachment: async (
    actionId,
    itemId,
    { file, description } = {}
  ) => {
    const formData = new FormData();
    if (!file) {
      throw new Error("فایل برای آپلود الزامی است");
    }
    formData.append("file", file);
    if (description) formData.append("description", description);

    return actionApiRequest(`actions/${actionId}/items/${itemId}/attachments`, {
      method: "POST",
      body: formData,
    });
  },

  // دریافت اطلاعات یک پیوست خاص آیتم
  getActionItemAttachment: async (actionId, itemId, attachmentId) => {
    return actionApiRequest(
      `actions/${actionId}/items/${itemId}/attachments/${attachmentId}`,
      {
        method: "GET",
      }
    );
  },

  // دانلود پیوست آیتم اقدام - برمی‌گرداند Blob
  downloadActionItemAttachment: async (actionId, itemId, attachmentId) => {
    const actionToken = localStorage.getItem("actionToken");
    if (!actionToken) {
      throw new Error(
        "توکن ماژول اقدام یافت نشد. لطفاً ابتدا وارد ماژول اقدام شوید."
      );
    }
    const url = getActionApiUrl(
      `actions/${actionId}/items/${itemId}/attachments/${attachmentId}/download`
    );

    const response = await fetch(url, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${actionToken}`,
      },
    });

    if (!response.ok) {
      throw new Error(`خطا در دانلود فایل (status: ${response.status})`);
    }

    const blob = await response.blob();
    return blob;
  },

  // به‌روزرسانی توضیحات پیوست آیتم
  updateActionItemAttachmentDescription: async (
    actionId,
    itemId,
    attachmentId,
    description
  ) => {
    return actionApiRequest(
      `actions/${actionId}/items/${itemId}/attachments/${attachmentId}/description`,
      {
        method: "PATCH",
        body: JSON.stringify({ description }),
      }
    );
  },

  // حذف پیوست آیتم اقدام
  deleteActionItemAttachment: async (actionId, itemId, attachmentId) => {
    return actionApiRequest(
      `actions/${actionId}/items/${itemId}/attachments/${attachmentId}`,
      {
        method: "DELETE",
      }
    );
  },

  // -------------------- Action Item Comments --------------------
  // دریافت لیست نظرات یک آیتم اقدام
  getActionItemComments: async (actionId, itemId) => {
    return actionApiRequest(`actions/${actionId}/items/${itemId}/comments`, {
      method: "GET",
    });
  },

  // ایجاد نظر جدید برای آیتم اقدام
  createActionItemComment: async (actionId, itemId, data) => {
    return actionApiRequest(`actions/${actionId}/items/${itemId}/comments`, {
      method: "POST",
      body: JSON.stringify(data),
    });
  },

  // دریافت یک نظر خاص آیتم
  getActionItemComment: async (actionId, itemId, commentId) => {
    return actionApiRequest(
      `actions/${actionId}/items/${itemId}/comments/${commentId}`,
      {
        method: "GET",
      }
    );
  },

  // به‌روزرسانی نظر آیتم
  updateActionItemComment: async (actionId, itemId, commentId, content) => {
    return actionApiRequest(
      `actions/${actionId}/items/${itemId}/comments/${commentId}`,
      {
        method: "PUT",
        body: JSON.stringify({ content }),
      }
    );
  },

  // حذف نظر آیتم
  deleteActionItemComment: async (actionId, itemId, commentId) => {
    return actionApiRequest(
      `actions/${actionId}/items/${itemId}/comments/${commentId}`,
      {
        method: "DELETE",
      }
    );
  },

  // دریافت لیست پاسخ‌های یک نظر آیتم
  getActionItemCommentReplies: async (actionId, itemId, commentId) => {
    return actionApiRequest(
      `actions/${actionId}/items/${itemId}/comments/${commentId}/replies`,
      {
        method: "GET",
      }
    );
  },

  // -------------------- Integration --------------------
  // همگام‌سازی کاربر از ERP به Action
  syncUserFromErp: async (erpUserId) => {
    return actionApiRequest("integration/erp/sync-user", {
      method: "POST",
      body: JSON.stringify({
        erp_user_id: String(erpUserId),
      }),
    });
  },

  // -------------------- Timeline --------------------
  // دریافت تایم‌لاین اقدام (بدون pagination)
  getActionTimeline: async (
    actionId,
    { event_type, user_id, date_from, date_to } = {}
  ) => {
    const params = new URLSearchParams();

    // event_type می‌تواند آرایه یا رشته جدا شده با کاما باشد
    if (event_type) {
      if (Array.isArray(event_type)) {
        event_type.forEach((t) => params.append("event_type[]", t));
      } else if (typeof event_type === "string" && event_type.includes(",")) {
        // رشته جدا شده با کاما
        params.append("event_type", event_type);
      } else {
        // رشته ساده
        params.append("event_type[]", event_type);
      }
    }

    const optionalParams = { user_id, date_from, date_to };
    Object.entries(optionalParams).forEach(([key, val]) => {
      if (val !== null && val !== undefined && val !== "") {
        params.append(key, String(val));
      }
    });

    const qs = params.toString();
    const endpoint = qs
      ? `actions/${actionId}/timeline?${qs}`
      : `actions/${actionId}/timeline`;
    return actionApiRequest(endpoint, { method: "GET" });
  },

  // -------------------- Audit Logs --------------------
  // دریافت لاگ حسابرسی اقدام (با pagination)
  getActionAuditLogs: async (
    actionId,
    { event_type, user_id, date_from, date_to, page, per_page } = {}
  ) => {
    const params = new URLSearchParams();

    // event_type می‌تواند آرایه یا رشته باشد
    if (event_type) {
      if (Array.isArray(event_type)) {
        event_type.forEach((t) => params.append("event_type[]", t));
      } else {
        params.append("event_type", event_type);
      }
    }

    const optionalParams = { user_id, date_from, date_to, page, per_page };
    Object.entries(optionalParams).forEach(([key, val]) => {
      if (val !== null && val !== undefined && val !== "") {
        params.append(key, String(val));
      }
    });

    const qs = params.toString();
    const endpoint = qs
      ? `actions/${actionId}/audit-logs?${qs}`
      : `actions/${actionId}/audit-logs`;
    return actionApiRequest(endpoint, { method: "GET" });
  },

  // -------------------- RACI --------------------
  // دریافت لیست تخصیص‌های RACI یک اقدام
  getRacis: async (actionId) => {
    return actionApiRequest(`actions/${actionId}/racis`, { method: "GET" });
  },

  // ایجاد تخصیص‌های RACI برای اقدام
  createRacis: async (actionId, assignments = []) => {
    return actionApiRequest(`actions/${actionId}/racis`, {
      method: "POST",
      body: JSON.stringify({ assignments }),
    });
  },

  // دریافت کاربران بر اساس نقش RACI برای یک اقدام
  getRaciUsersByRole: async (actionId, role) => {
    return actionApiRequest(`actions/${actionId}/racis/users/${role}`, {
      method: "GET",
    });
  },

  // حذف یک تخصیص RACI
  deleteRaci: async (actionId, raciId) => {
    return actionApiRequest(`actions/${actionId}/racis/${raciId}`, {
      method: "DELETE",
    });
  },

  // -------------------- Notifications --------------------
  // دریافت لیست اعلان‌ها
  getNotifications: async (filters = {}) => {
    const params = new URLSearchParams();
    
    const {
      read,
      type,
      action_id,
      date_from,
      date_to,
      page,
      per_page,
      limit,
    } = filters;

    if (read !== undefined && read !== null && read !== '') {
      params.append('read', String(read));
    }
    if (type) {
      params.append('type', String(type));
    }
    if (action_id) {
      params.append('action_id', String(action_id));
    }
    if (date_from) {
      params.append('date_from', String(date_from));
    }
    if (date_to) {
      params.append('date_to', String(date_to));
    }
    if (page) {
      params.append('page', String(page));
    }
    if (per_page) {
      params.append('per_page', String(per_page));
    }
    if (limit) {
      params.append('limit', String(limit));
    }

    const qs = params.toString();
    const endpoint = qs
      ? `notifications?${qs}`
      : `notifications`;
    return actionApiRequest(endpoint, { method: "GET" });
  },

  // دریافت تعداد اعلان‌های خوانده نشده
  getUnreadCount: async () => {
    const actionToken = localStorage.getItem("actionToken");
    if (!actionToken) {
      // اگر توکن وجود نداشت، مقدار پیش‌فرض برمی‌گردانیم
      return { success: true, data: { unread_count: 0 } };
    }
    return actionApiRequest("notifications/unread-count", { method: "GET" });
  },

  // علامت‌گذاری همه اعلان‌ها به عنوان خوانده شده
  markAllAsRead: async () => {
    return actionApiRequest("notifications/mark-all-read", {
      method: "POST",
    });
  },

  // دریافت تنظیمات اعلان
  getNotificationPreferences: async () => {
    return actionApiRequest("notifications/preferences", { method: "GET" });
  },

  // به‌روزرسانی تنظیمات اعلان
  updateNotificationPreferences: async (data) => {
    // data باید شامل { preferences: { ... } } باشد
    return actionApiRequest("notifications/preferences", {
      method: "PUT",
      body: JSON.stringify(data),
    });
  },

  // بازنشانی تنظیمات اعلان به حالت اولیه
  resetNotificationPreferences: async () => {
    return actionApiRequest("notifications/preferences/reset", {
      method: "POST",
    });
  },

  // دریافت لیست کاربران (برای فیلتر درخواست‌کننده)
  getUsers: async ({ search, per_page = 15 } = {}) => {
    const params = new URLSearchParams();
    if (search) params.append("search", search);
    if (per_page) params.append("per_page", per_page);

    const qs = params.toString();
    const endpoint = qs ? `users?${qs}` : "users";
    return actionApiRequest(endpoint, { method: "GET" });
  },

  // دریافت یک اعلان خاص
  getNotification: async (notificationId) => {
    return actionApiRequest(`notifications/${notificationId}`, {
      method: "GET",
    });
  },

  // علامت‌گذاری اعلان به عنوان خوانده شده
  markNotificationAsRead: async (notificationId) => {
    return actionApiRequest(`notifications/${notificationId}/read`, {
      method: "PATCH",
    });
  },

  // علامت‌گذاری اعلان به عنوان خوانده نشده
  markNotificationAsUnread: async (notificationId) => {
    return actionApiRequest(`notifications/${notificationId}/unread`, {
      method: "PATCH",
    });
  },

  // حذف اعلان
  deleteNotification: async (notificationId) => {
    return actionApiRequest(`notifications/${notificationId}`, {
      method: "DELETE",
    });
  },

  // -------------------- Reports --------------------
  // دریافت گزارش کلی اقدامات
  getActionsReport: async (filters = {}) => {
    const params = new URLSearchParams();
    const optionalParams = {
      status: filters.status,
      priority: filters.priority,
      action_type: filters.action_type,
      source_module: filters.source_module,
      requester_id: filters.requester_id,
      assigned_to: filters.assigned_to,
      raci_role: filters.raci_role,
      raci_user_id: filters.raci_user_id,
      date_from: filters.date_from,
      date_to: filters.date_to,
      search: filters.search,
      overdue: filters.overdue,
      sort: filters.sort,
      order: filters.order,
      page: filters.page,
      per_page: filters.per_page,
    };

    Object.entries(optionalParams).forEach(([key, val]) => {
      if (val !== null && val !== undefined && val !== "") {
        if (key === "overdue" && typeof val === "boolean") {
          // برای overdue از 0 و 1 استفاده می‌کنیم
          params.append(key, val ? "1" : "0");
        } else if (typeof val === "boolean") {
          params.append(key, val ? "true" : "false");
        } else {
          params.append(key, String(val));
        }
      }
    });

    const qs = params.toString();
    const endpoint = qs
      ? `actions/reports?${qs}`
      : `actions/reports`;
    return actionApiRequest(endpoint, { method: "GET" });
  },

  // دریافت گزارش پیشرفت
  getActionsProgressReport: async (filters = {}) => {
    const params = new URLSearchParams();
    const optionalParams = {
      status: filters.status,
      priority: filters.priority,
      action_type: filters.action_type,
      source_module: filters.source_module,
      requester_id: filters.requester_id,
      assigned_to: filters.assigned_to,
      raci_role: filters.raci_role,
      raci_user_id: filters.raci_user_id,
      date_from: filters.date_from,
      date_to: filters.date_to,
      search: filters.search,
      overdue: filters.overdue,
      sort: filters.sort,
      order: filters.order,
    };

    Object.entries(optionalParams).forEach(([key, val]) => {
      if (val !== null && val !== undefined && val !== "") {
        if (key === "overdue" && typeof val === "boolean") {
          // برای overdue از 0 و 1 استفاده می‌کنیم
          params.append(key, val ? "1" : "0");
        } else if (typeof val === "boolean") {
          params.append(key, val ? "true" : "false");
        } else {
          params.append(key, String(val));
        }
      }
    });

    const qs = params.toString();
    const endpoint = qs
      ? `actions/reports/progress?${qs}`
      : `actions/reports/progress`;
    return actionApiRequest(endpoint, { method: "GET" });
  },

  // دریافت آمار اقدامات
  getActionsStatistics: async (filters = {}) => {
    const params = new URLSearchParams();
    const optionalParams = {
      status: filters.status,
      priority: filters.priority,
      action_type: filters.action_type,
      source_module: filters.source_module,
      requester_id: filters.requester_id,
      assigned_to: filters.assigned_to,
      raci_role: filters.raci_role,
      raci_user_id: filters.raci_user_id,
      date_from: filters.date_from,
      date_to: filters.date_to,
      search: filters.search,
      overdue: filters.overdue,
      sort: filters.sort,
      order: filters.order,
    };

    Object.entries(optionalParams).forEach(([key, val]) => {
      if (val !== null && val !== undefined && val !== "") {
        if (key === "overdue" && typeof val === "boolean") {
          // برای overdue از 0 و 1 استفاده می‌کنیم
          params.append(key, val ? "1" : "0");
        } else if (typeof val === "boolean") {
          params.append(key, val ? "true" : "false");
        } else {
          params.append(key, String(val));
        }
      }
    });

    const qs = params.toString();
    const endpoint = qs
      ? `actions/reports/statistics?${qs}`
      : `actions/reports/statistics`;
    return actionApiRequest(endpoint, { method: "GET" });
  },

  // خروجی گرفتن از اقدامات
  exportActions: async (filters = {}) => {
    const params = new URLSearchParams();
    const optionalParams = {
      format: filters.format,
      status: filters.status,
      priority: filters.priority,
      action_type: filters.action_type,
      source_module: filters.source_module,
      requester_id: filters.requester_id,
      assigned_to: filters.assigned_to,
      raci_role: filters.raci_role,
      raci_user_id: filters.raci_user_id,
      date_from: filters.date_from,
      date_to: filters.date_to,
      search: filters.search,
      overdue: filters.overdue,
      sort: filters.sort,
      order: filters.order,
    };

    Object.entries(optionalParams).forEach(([key, val]) => {
      if (val !== null && val !== undefined && val !== "") {
        if (key === "overdue" && typeof val === "boolean") {
          // برای overdue از 0 و 1 استفاده می‌کنیم
          params.append(key, val ? "1" : "0");
        } else if (typeof val === "boolean") {
          params.append(key, val ? "true" : "false");
        } else {
          params.append(key, String(val));
        }
      }
    });

    const qs = params.toString();
    const endpoint = qs
      ? `actions/export?${qs}`
      : `actions/export`;
    return actionApiRequest(endpoint, { method: "POST" });
  },

  // خروجی گرفتن از گزارش پیشرفت
  exportActionsProgress: async (filters = {}) => {
    const params = new URLSearchParams();
    const optionalParams = {
      format: filters.format,
      status: filters.status,
      priority: filters.priority,
      action_type: filters.action_type,
      source_module: filters.source_module,
      requester_id: filters.requester_id,
      assigned_to: filters.assigned_to,
      raci_role: filters.raci_role,
      raci_user_id: filters.raci_user_id,
      date_from: filters.date_from,
      date_to: filters.date_to,
      search: filters.search,
      overdue: filters.overdue,
      sort: filters.sort,
      order: filters.order,
    };

    Object.entries(optionalParams).forEach(([key, val]) => {
      if (val !== null && val !== undefined && val !== "") {
        if (key === "overdue" && typeof val === "boolean") {
          // برای overdue از 0 و 1 استفاده می‌کنیم
          params.append(key, val ? "1" : "0");
        } else if (typeof val === "boolean") {
          params.append(key, val ? "true" : "false");
        } else {
          params.append(key, String(val));
        }
      }
    });

    const qs = params.toString();
    const endpoint = qs
      ? `actions/export/progress?${qs}`
      : `actions/export/progress`;
    return actionApiRequest(endpoint, { method: "POST" });
  },

  // دانلود فایل export
  downloadExportFile: async (filePath) => {
    const actionToken = localStorage.getItem("actionToken");

    if (!actionToken) {
      throw new Error(
        "توکن ماژول اقدام یافت نشد. لطفاً ابتدا وارد ماژول اقدام شوید."
      );
    }

    // اگر filePath با / شروع نشده باشد، آن را اضافه می‌کنیم
    const normalizedPath = filePath.startsWith("/") ? filePath : `/${filePath}`;
    const endpoint = `actions/export/download${normalizedPath}`;
    const url = getActionApiUrl(endpoint);

    console.log("🔵 Action API Download Request:", {
      url,
      filePath,
    });

    try {
      const response = await fetch(url, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${actionToken}`,
        },
      });

      console.log("🟢 Action API Download Response:", {
        url,
        status: response.status,
        statusText: response.statusText,
        ok: response.ok,
        contentType: response.headers.get("content-type"),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error("🔴 Action API Download Error:", {
          url,
          status: response.status,
          errorData,
        });

        // هندل خطاهای 401 (Unauthorized)
        if (response.status === 401) {
          try {
            if (!window.__ACTION_UNAUTHORIZED_REDIRECTING__) {
              window.__ACTION_UNAUTHORIZED_REDIRECTING__ = true;
              localStorage.removeItem("actionToken");
              localStorage.removeItem("actionUserData");
              if (window.location.pathname !== "/dashboard") {
                window.location.replace("/dashboard");
              }
            }
          } catch (e) {
            console.error("Error during 401 redirect:", e);
          }
          throw new Error(
            errorData.message ||
              errorData.error?.message ||
              "Unauthorized - دسترسی غیرمجاز"
          );
        }

        throw new Error(
          errorData.message ||
            errorData.error?.message ||
            `HTTP error! status: ${response.status}`
        );
      }

      // دریافت فایل به صورت blob
      const blob = await response.blob();

      // استخراج نام فایل از header یا filePath
      const contentDisposition = response.headers.get("content-disposition");
      let filename = "export.xlsx";
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1].replace(/['"]/g, "");
          // decode URI component if needed
          try {
            filename = decodeURIComponent(filename);
          } catch (e) {
            // ignore decode error
          }
        }
      } else if (filePath) {
        // استفاده از نام فایل از filePath
        const pathParts = filePath.split("/");
        filename = pathParts[pathParts.length - 1] || "export.xlsx";
      }

      // ایجاد لینک دانلود و کلیک خودکار
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);

      console.log("✅ File downloaded successfully:", filename);

      return {
        success: true,
        filename,
        blob,
      };
    } catch (error) {
      console.error("🔴 Action API Download Request Failed:", {
        url,
        error: error.message,
        errorObject: error,
      });
      throw error;
    }
  },
};

export default {
  actionApiRequest,
  actionApi,
  ActionValidationError,
  decodeUnicodeEscapes,
  logWithPersianText,
};
